import { useEffect, useMemo, useState } from 'react';
import Image from 'next/legacy/image';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { withRouter } from 'next/router';
import type { NextRouter } from 'next/router';
import { generateDesktopNavLinks, insertGameWeek } from './utils';
import {
  DesktopLoginButton,
  JoinTheHubButton,
  MobileLoginButton,
  MobileMenuButton,
  SignOutButton
} from './UIElements/Button';
import UltraBadge from './UIElements/UltraBadge';
import NavagationAccordion from '../navigation-accordion';
import { useAuth0User } from '@hooks/useAuth0User';
import { useLeagueContext } from '@hooks/useLeagueContext';
import type { NavBarType } from '@lib/constants';
import {
  buildNavItems,
  isVisibleToAudience
} from '@lib/navigation/site-navigation';
import type { SiteNavigation } from '@lib/navigation/types';
import {
  isCurrentPath,
  isInActiveHierachy,
  isUltraMember,
  isUserAuthenticated
} from '@lib/utils';
import AnimateFootball from '@components/animate-football';
import Icon from '@components/atoms/icon';
import SubMenu from '@components/sub-menu';
import { useNotificationPreferencesFeatureFlag } from '@hooks/notifications';

// Under Turbopack, next/dynamic components rendered from _app aren't preloaded
// before hydration, so anything that renders on the server must be a static
// import (FFH-3175). The modal can stay dynamic: it renders nothing until opened.
const SearchModal = dynamic(() => import('@components/search/SearchModal'));

type HeaderProps = {
  router: NextRouter;
  /** The editors' menus from Sanity; null or missing renders NAV_DATA's. */
  siteNavigation?: SiteNavigation | null;
};

const Header = ({ router, siteNavigation }: HeaderProps) => {
  const { user, isLoading } = useAuth0User();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [searchModalIsOpen, setSearchModalIsOpen] = useState(false);
  const [joinButtonText, setJoinButtonText] = useState<string>('');
  const [subMenuActiveLevel, setSubMenuActiveLevel] = useState(undefined);

  const { context } = useLeagueContext();
  const nextGameweek = context?.nextGameweek?.number ?? null;

  useEffect(() => {
    if (!isLoading && isUserAuthenticated(user)) {
      setJoinButtonText('Upgrade');
    } else {
      setJoinButtonText('Join The Hub');
    }
  }, [isLoading, user]);

  const closeSidebar = () => {
    setShowMobileMenu(false);
  };

  const toggle = () => {
    setShowMobileMenu(!showMobileMenu);
  };

  const hasPath = (path) => path != '/';

  const hasPemissionToViewSubmenuItem = (item) =>
    !item.isHiddenForGuests ||
    (item.isHiddenForGuests && isUserAuthenticated(user));

  const path = router.asPath.split('?')[0];
  // Where the login links send the user back to once they have signed in
  // (FFH-2135). The home page is the callback's own default, so it needs no
  // returnTo of its own.
  const returnTo = hasPath(router.asPath) ? router.asPath : undefined;

  const isNotificationsEnabled = useNotificationPreferencesFeatureFlag();

  const navItems = useMemo(
    () => buildNavItems(siteNavigation),
    [siteNavigation]
  );
  const audienceContext = {
    isAuthenticated: isUserAuthenticated(user),
    isUltra: isUltraMember(user)
  };

  const navData = navItems
    .map((data) => {
      if (!isVisibleToAudience(data.audience, audienceContext)) {
        return false;
      }
      if (data.name === 'Notifications' && (!isNotificationsEnabled || !user)) {
        return false;
      }
      if (data?.hasGameweek) {
        return {
          ...data,
          name: insertGameWeek(data?.name, nextGameweek)
        };
      }

      return {
        ...data
      };
    })
    .filter(Boolean);

  const navLinks = navData.filter((item): item is NavBarType => Boolean(item));

  // Divider entries stay in the list - the sub-menu uses them as group
  // headings in its all-tools panel and sheet
  const subnavigationItems = navLinks
    .filter(
      (a) =>
        isInActiveHierachy(path, a, navLinks) && !a.isTopLevel && !a.hideSubNav
    )
    .filter((item) => hasPemissionToViewSubmenuItem(item));

  const activeTopLevelNavItem = navLinks.find(
    (item) => item.isTopLevel && isInActiveHierachy(path, item, navLinks)
  );

  return (
    <div className="border-b border-accent-3">
      <SearchModal
        open={searchModalIsOpen}
        onClose={() => setSearchModalIsOpen(false)}
        onNavigate={() => setShowMobileMenu(false)}
        nextGameweek={nextGameweek}
      />
      <div
        className={`lg:hidden nav nav-accordion  ${
          showMobileMenu ? 'nav--open' : ''
        }`}
      >
        <div className="nav__links flex flex-col">
          <div>
            <div className="flex justify-between items-center pt-5 px-4 pb-6">
              <Link
                href="/"
                className="flex lg:mr-2 xl:mr-24 h-[50px] w-[50px]"
              >
                <Image
                  src="/images/hub-logo.svg"
                  alt="Fantasy Football Hub"
                  height={50}
                  width={50}
                />
              </Link>
              <Icon
                name="Close"
                className="text-white h-6 w-6"
                onClick={() => closeSidebar()}
              ></Icon>
            </div>
            <NavagationAccordion
              closeSidebar={() => closeSidebar()}
              data={navData}
              setSearchModalIsOpen={setSearchModalIsOpen}
            />
          </div>
          <div className="px-4 pb-8">
            {!isUserAuthenticated(user) ? (
              <div className="flex space-x-3">
                <JoinTheHubButton
                  onClick={() => closeSidebar()}
                  path={path}
                  text={joinButtonText}
                />
                <MobileLoginButton
                  onClick={() => closeSidebar()}
                  returnTo={returnTo}
                >
                  Log in
                </MobileLoginButton>
              </div>
            ) : (
              <div className="flex space-x-3">
                <JoinTheHubButton
                  onClick={() => closeSidebar()}
                  path={path}
                  text={joinButtonText}
                />
                {/* eslint-disable-next-line @next/next/no-html-link-for-pages -- Using <a> to avoid Next.js prefetching which would trigger logout */}
                <a
                  data-cy="mobile-menu-logout"
                  onClick={() => closeSidebar()}
                  href="/auth/logout"
                  className="rounded-lg text-white py-2 px-3 font-bold font-open-sans bg-transparent border-2 border-white"
                >
                  Log out
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
      <nav className="bg-neutral-800 fixed w-full z-50 shadow-300">
        <div className="xl:container max-w-screen-3xl px-4 sm:px-8 md:px-4 mx-auto py-3">
          <div className="relative flex items-center justify-between scrolling-touch no-scrollbar whitespace-nowrap">
            <div className="flex items-center">
              <Link
                href="/"
                className="flex lg:mr-2 xl:mr-24 h-[50px] w-[50px]"
              >
                <Image
                  src="/images/hub-logo.svg"
                  alt="Fantasy Football Hub"
                  height={50}
                  width={50}
                />
              </Link>
              <div className="hidden lg:block">
                <div className="flex">
                  {generateDesktopNavLinks({
                    navData,
                    isUserAuthenticated,
                    status: user,
                    isCurrentPath: (currentPath, item) =>
                      isCurrentPath(currentPath, item, navLinks),
                    path,
                    subMenuActiveLevel,
                    setSubMenuActiveLevel
                  })}
                </div>
              </div>
            </div>
            <div className="flex flex-row items-center justify-end">
              <div className="flex-1 hidden px-2 md:hidden lg:ml-6 lg:justify-end">
                <div className="w-full md:max-w-md lg:max-w-xs">
                  <div className="relative">
                    <Icon
                      name={'Search'}
                      style={{ fontSize: '24px' }}
                      className="text-white cursor-pointer"
                      onClick={() => setSearchModalIsOpen(true)}
                    />
                  </div>
                </div>
              </div>
              {isLoading && (
                <div className="w-full lg:hidden">
                  <AnimateFootball height={24} />
                </div>
              )}
              <div className="flex flex-1 px-3 items-center lg:justify-end">
                <div className="flex items-center justify-between w-full lg:w-fit">
                  <div className="relative hidden lg:block">
                    <Icon
                      name={'Search'}
                      style={{ fontSize: '24px' }}
                      className="text-white cursor-pointer hover:scale-105"
                      onClick={() => setSearchModalIsOpen(true)}
                    />
                  </div>
                  <div className="block lg:hidden">
                    {isUltraMember(user) ? (
                      <UltraBadge />
                    ) : (
                      <JoinTheHubButton
                        onClick={() => closeSidebar()}
                        path={path}
                        text={joinButtonText}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="justify-end divide-x-0 block lg:hidden">
                <MobileMenuButton
                  showMobileMenu={showMobileMenu}
                  toggle={toggle}
                />
              </div>
              <div className="hidden lg:contents lg:ml-4">
                {isLoading ? (
                  <div className="w-full">
                    <AnimateFootball height={24} />
                  </div>
                ) : (
                  <div className="flex items-center account-menu">
                    {!isUserAuthenticated(user) ? (
                      <>
                        <DesktopLoginButton returnTo={returnTo}>
                          Log in
                        </DesktopLoginButton>
                        <JoinTheHubButton
                          onClick={() => closeSidebar()}
                          path={path}
                          text={joinButtonText}
                        />
                      </>
                    ) : (
                      <>
                        <Link
                          href="/account"
                          aria-label="Account"
                          className="inline-flex pointer mr-5"
                        >
                          <Icon
                            className="text-white w-6 h-6"
                            name="Person"
                            title="Account"
                          ></Icon>
                        </Link>
                        {isUltraMember(user) ? (
                          <UltraBadge />
                        ) : (
                          <JoinTheHubButton
                            onClick={() => closeSidebar()}
                            path={path}
                            text="Upgrade"
                          />
                        )}
                        <SignOutButton
                          onClick={() => {
                            window.location.href = '/auth/logout';
                          }}
                        />
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>
      <div className="pt-[74px]">
        {path !== '/' &&
          subnavigationItems.some((item) => Boolean(item.url)) && (
            <SubMenu
              items={subnavigationItems}
              label={activeTopLevelNavItem?.name}
            />
          )}
      </div>
    </div>
  );
};

export default withRouter(Header);
